import {
  FileCode2Icon,
  GitPullRequestIcon,
  MessageSquareTextIcon,
  NetworkIcon,
} from "lucide-react";
import type { CmmdProjectLeadBriefing } from "@cmmd-forge/contracts";
import { ProjectLeadNeedsYou } from "./ProjectLeadNeedsYou";

interface BriefingMetricProps {
  readonly label: string;
  readonly value: number;
}

function BriefingMetric({ label, value }: BriefingMetricProps) {
  return (
    <div className="min-w-0 border-l border-border pl-3 first:border-l-0 first:pl-0">
      <div className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function EmptyBriefingRow({ children }: { readonly children: React.ReactNode }) {
  return <p className="text-xs leading-5 text-muted-foreground">{children}</p>;
}

function BriefingSectionHeader({
  icon: Icon,
  title,
}: {
  readonly icon: typeof NetworkIcon;
  readonly title: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="size-4 text-muted-foreground" aria-hidden />
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    </div>
  );
}

function ProjectPulse({ briefing }: { readonly briefing: CmmdProjectLeadBriefing }) {
  const staffedTasks = briefing.tasks.filter((task) => task.activeAgentIds.length > 0);
  const blockedTasks = briefing.tasks.filter((task) => task.blockedByCount > 0);

  return (
    <section aria-labelledby="lead-briefing-coverage-heading" className="space-y-3">
      <div className="flex items-center gap-2">
        <NetworkIcon className="size-4 text-muted-foreground" aria-hidden />
        <h2 id="lead-briefing-coverage-heading" className="text-sm font-semibold text-foreground">
          Board at a glance
        </h2>
      </div>
      <p className="text-xs leading-5 text-muted-foreground">
        A quick read of the board. Detailed conversations and repository proof stay with the work.
      </p>
      <div className="grid grid-cols-3 gap-3 border-y border-border py-3">
        <BriefingMetric label="Tasks" value={briefing.tasks.length} />
        <BriefingMetric label="Staffed" value={staffedTasks.length} />
        <BriefingMetric label="Blocked" value={blockedTasks.length} />
      </div>
      {blockedTasks.length > 0 ? (
        <p className="text-xs leading-5 text-warning-foreground">
          {blockedTasks.length} task{blockedTasks.length === 1 ? " is" : "s are"} blocked. Open the
          relevant task to inspect its dependencies and next action.
        </p>
      ) : (
        <EmptyBriefingRow>No blocked tasks are in the current project snapshot.</EmptyBriefingRow>
      )}
    </section>
  );
}

function CoordinationMessages({ briefing }: { readonly briefing: CmmdProjectLeadBriefing }) {
  return (
    <section
      aria-labelledby="lead-briefing-messages-heading"
      className="space-y-3 border-t border-border pt-5"
    >
      <BriefingSectionHeader icon={MessageSquareTextIcon} title="Coordination updates" />
      {briefing.coordinationMessages.length > 0 ? (
        <div className="space-y-3">
          {briefing.coordinationMessages.slice(0, 3).map((message) => (
            <div key={message.id} className="space-y-1 text-xs">
              <p className="font-medium text-foreground">
                Task {message.taskId} · {message.kind}
              </p>
              <p className="line-clamp-2 leading-5 text-muted-foreground">{message.body}</p>
              <p className="text-[11px] text-muted-foreground">
                {message.deliveryStatuses.join(", ")}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyBriefingRow>No agent coordination messages have been recorded.</EmptyBriefingRow>
      )}
    </section>
  );
}

function RisksAndQuestions({ briefing }: { readonly briefing: CmmdProjectLeadBriefing }) {
  const openRisks = briefing.risks.filter((risk) => risk.status !== "resolved");
  const openQuestions = briefing.questions.filter((question) => question.status === "open");
  return (
    <section
      aria-labelledby="lead-briefing-risks-heading"
      className="space-y-3 border-t border-border pt-5"
    >
      <BriefingSectionHeader icon={NetworkIcon} title="Risks & questions" />
      {openRisks.length + openQuestions.length > 0 ? (
        <div className="space-y-2 text-xs">
          {openRisks.slice(0, 2).map((risk) => (
            <div key={`risk:${risk.id}`}>
              <p className="font-medium text-foreground">{risk.title}</p>
              <p className="mt-0.5 text-muted-foreground">
                Risk · {risk.severity} · {risk.status}
              </p>
            </div>
          ))}
          {openQuestions.slice(0, 2).map((question) => (
            <div key={`question:${question.id}`}>
              <p className="font-medium text-foreground">{question.prompt}</p>
              <p className="mt-0.5 text-muted-foreground">Open question</p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyBriefingRow>No open risks or questions are recorded.</EmptyBriefingRow>
      )}
    </section>
  );
}

function ConversationLinks({ briefing }: { readonly briefing: CmmdProjectLeadBriefing }) {
  return (
    <section
      aria-labelledby="lead-briefing-conversations-heading"
      className="space-y-3 border-t border-border pt-5"
    >
      <BriefingSectionHeader icon={MessageSquareTextIcon} title="Work conversations" />
      {briefing.conversations.length > 0 ? (
        <div className="space-y-2">
          {briefing.conversations.slice(0, 4).map((conversation) => (
            <div key={conversation.conversationId} className="text-xs">
              <p className="truncate font-medium text-foreground">{conversation.title}</p>
              <p className="mt-0.5 text-muted-foreground">
                Task {conversation.taskId} · {conversation.status}
                {conversation.branch === null ? "" : ` · ${conversation.branch}`}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyBriefingRow>No task-linked conversation metadata is available.</EmptyBriefingRow>
      )}
    </section>
  );
}

function RepositoryProof({ briefing }: { readonly briefing: CmmdProjectLeadBriefing }) {
  return (
    <section
      aria-labelledby="lead-briefing-proof-heading"
      className="space-y-3 border-t border-border pt-5"
    >
      <BriefingSectionHeader icon={FileCode2Icon} title="Repository proof" />
      {briefing.repositoryEvidence.length > 0 ? (
        <div className="space-y-2">
          {briefing.repositoryEvidence.slice(0, 3).map((evidence) => (
            <div key={evidence.id} className="text-xs">
              <p className="font-medium text-foreground">{evidence.summary}</p>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                Task {evidence.taskId} ·{" "}
                {evidence.commitSha?.slice(0, 12) ?? evidence.refName ?? evidence.kind}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyBriefingRow>No repository evidence has been linked to this project.</EmptyBriefingRow>
      )}
      {briefing.pullRequests.length > 0 ? (
        <div className="space-y-2 border-t border-border pt-3">
          <BriefingSectionHeader icon={GitPullRequestIcon} title="Pull requests" />
          {briefing.pullRequests.slice(0, 3).map((pullRequest) => (
            <a
              key={pullRequest.id}
              href={pullRequest.url}
              target="_blank"
              rel="noreferrer"
              className="block text-xs text-foreground underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              #{pullRequest.number} · {pullRequest.title}
            </a>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function ProjectLeadBriefingSection({
  briefing,
  onDecideApproval,
}: {
  readonly briefing: CmmdProjectLeadBriefing;
  readonly onDecideApproval: (
    approvalId: string,
    decision: "approved" | "denied",
    reason: string,
  ) => Promise<void>;
}) {
  return (
    <>
      <ProjectLeadNeedsYou briefing={briefing} onDecideApproval={onDecideApproval} />
      <RisksAndQuestions briefing={briefing} />
      <ProjectPulse briefing={briefing} />
      <CoordinationMessages briefing={briefing} />
      <ConversationLinks briefing={briefing} />
      <RepositoryProof briefing={briefing} />
    </>
  );
}
